import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

// Shared wrapper for the jQuery DataTables-backed admin tables. Every DOM lookup is scoped to
// this controller's own mount, so one page can carry several tables without cross-wiring them.
//
// Mount `data-controller` on a wrapping element, never on the `<table>` itself: DataTables
// detaches and re-wraps the table node on init, which Stimulus reads as a disconnect/connect and
// loops forever re-initializing.
export default class extends Controller {
  static get targets() {
    return ["table", "filterSelect"];
  }

  static get values() {
    return {
      source: String,
      order: Array,
      columns: Array,
      pageLength: { type: Number, default: 25 },
      filterParam: { type: String, default: "" },
      serverSide: { type: Boolean, default: true },
    };
  }

  connect() {
    this.dataTable = $(this.tableTarget).dataTable({
      processing: true,
      serverSide: this.serverSideValue,
      responsive: false,
      autoWidth: false,
      lengthMenu: [25, 50, 75, 100, 500, 1000],
      pageLength: this.pageLengthValue,
      dom: "<'dataTable_container'tr>" +
        "<'table-information'p<'page-information'li>>",
      pagingType: "full_numbers",
      language: {
        search: "",
        emptyTable: "No data available.",
        searchPlaceholder: "Search by name or email",
        lengthMenu: "Show _MENU_",
        info: "viewing _START_ - _END_ / _TOTAL_",
        paginate: { first: "First", last: "Last", next: "Next", previous: "Prev" },
      },
      ajax: { url: this.sourceValue },
      order: this.orderValue,
      columns: this.columnsValue,
      fnDrawCallback: (settings) => this.onDraw(settings),
    });

    this.bindSearchInput();
  }

  disconnect() {
    // The debounce is up to 500ms behind the teardown, and it reaches for a DataTable instance
    // that is about to stop existing.
    clearTimeout(this.searchTimer);
    this.searchInputs().off("input.datatable");
    // Turbo can restore this <table> from cache without a fresh boot, and `.dataTable()` throws
    // if it runs again on an element that already has an instance.
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      $(this.tableTarget).DataTable().destroy();
    }
  }

  // Public: called by other controllers on the page. Keeps the current page rather than
  // resetting to page 1.
  reload() {
    if (!this.hasTableTarget) { return; }
    $(this.tableTarget).DataTable().ajax.reload(null, false);
  }

  // No current filter markup emits the literal "all" (they use an empty value), so that branch is
  // dead today; it is kept because the option lists are rendered per-page.
  onFilterChange(event) {
    const filterValue = event.currentTarget.value;
    const url = filterValue === "all" ?
      this.sourceValue :
      `${this.sourceValue}?${this.filterParamValue}=${filterValue}`;

    ProgressBar.start();
    $(this.tableTarget).DataTable().ajax.url(url).load(() => ProgressBar.stop());
  }

  onDraw(settings) {
    const $wrapper = $(settings.nTableWrapper);

    $wrapper.find(".paginate_button.first a, .paginate_button.last a").addClass("btn btn-main btn-ghost");

    const $pages = $wrapper.find(".paginate_button:not(.previous, .next, .first, .last)");
    if ($pages.length > 1) {
      $pages.first().addClass("first-page");
      $pages.last().addClass("last-page");
    }

    $wrapper.find(".paginate_button.previous a").html("<i class='icon-lasso-chevron-left'></i>");
    $wrapper.find(".paginate_button.next a").html("<i class='icon-lasso-chevron-right'></i>");

    if (settings._iDisplayLength > settings.fnRecordsDisplay()) {
      $wrapper.find(".dataTables_paginate").hide();
    } else {
      $wrapper.find(".dataTables_paginate").show();
    }

    // `id` arrives raw off the query string. Interpolated into a `#…` selector an empty or
    // syntactically invalid value throws out of the draw callback, leaving the reskin above
    // half-applied, so match on the property instead of parsing a selector.
    const highlightId = new URL(window.location.href).searchParams.get("id");
    if (highlightId) {
      const row = Array.from(this.tableTarget.querySelectorAll("[id]")).
        find((element) => element.id === highlightId);
      row?.closest("tr")?.classList.add("highlighted");
    }
  }

  bindSearchInput() {
    this.searchInputs().off("input.datatable").on("input.datatable", (event) => {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        $(this.tableTarget).DataTable().search(event.currentTarget.value).draw();
      }, 500);
    });
  }

  // Rooted at the mount, not at `.dataTables_wrapper`: DataTables wraps only the `<table>` node,
  // so a search box sitting beside the table rather than inside it would never be found.
  searchInputs() {
    return $(this.element).
      find(".dataTables_filter input[type='search'], .search-bar input.datatable-search");
  }
}
